/**
 * Catálogo das medidas corporais (módulo "Medidas corporais").
 *
 * Chaves, unidades, grupos e posição na silhueta são fixos em código e espelham
 * `public/api/body-measurements/BodyMeasures.php`. O formulário pede todas as
 * medidas, nenhuma obrigatória; a tela mostra só as que tiverem valor.
 *
 * `min`/`max`: faixa plausível, o mesmo limite do backend. Fora dela o valor é
 * recusado, para nunca calcular IMC ou % de gordura a partir de um erro de
 * digitação (ex.: altura de 11 cm). As dobras cutâneas entram pelo protocolo
 * escolhido (ver components/bodyMeasurements/bodyComposition.js).
 */

export const BODY_MEASURE_GROUP = Object.freeze({
  INDICATORS: 'indicators',
  TRUNK: 'trunk',
  LIMBS: 'limbs',
  SKINFOLDS: 'skinfolds',
  DIAMETERS: 'diameters',
});

export const BODY_MEASURE_GROUPS = Object.freeze([
  { key: BODY_MEASURE_GROUP.INDICATORS, label: 'Indicadores' },
  { key: BODY_MEASURE_GROUP.TRUNK, label: 'Tronco' },
  { key: BODY_MEASURE_GROUP.LIMBS, label: 'Membros' },
  { key: BODY_MEASURE_GROUP.SKINFOLDS, label: 'Dobras cutâneas' },
  { key: BODY_MEASURE_GROUP.DIAMETERS, label: 'Diâmetros ósseos' },
]);

const { INDICATORS, TRUNK, LIMBS, SKINFOLDS, DIAMETERS } = BODY_MEASURE_GROUP;
const cm = (key, label, group, min, max, anchor) => ({ key, label, unit: 'cm', decimals: 1, group, min, max, ...(anchor ? { anchor: { side: anchor } } : {}) });
const mm = (key, label, group, min, max) => ({ key, label, unit: 'mm', decimals: 1, group, min, max });

/**
 * `anchor.side`: lado da etiqueta na silhueta (o ponto vem da região de mesma
 * chave em `bodySilhouetteFigures.js`). Medidas sem `anchor` (peso, altura,
 * gordura) viram cartões de indicador; as demais sem `anchor` (braço
 * contraído, dobras, diâmetros) aparecem abaixo da silhueta. O lado é o da
 * tela: o lado direito do paciente fica à esquerda de quem olha.
 */
export const BODY_MEASURES = Object.freeze([
  { key: 'weight', label: 'Peso', unit: 'kg', decimals: 1, group: INDICATORS, min: 0.5, max: 400 },
  cm('height', 'Altura', INDICATORS, 30, 250),
  { key: 'body_fat', label: 'Gordura corporal', unit: '%', decimals: 1, group: INDICATORS, min: 2, max: 70 },
  cm('neck', 'Pescoço', TRUNK, 10, 80, 'right'),
  cm('chest', 'Tórax', TRUNK, 25, 200, 'left'),
  cm('waist', 'Cintura', TRUNK, 25, 250, 'right'),
  cm('abdomen', 'Abdômen', TRUNK, 25, 250, 'left'),
  cm('hip', 'Quadril', TRUNK, 25, 250, 'right'),
  cm('arm_right', 'Braço direito', LIMBS, 8, 80, 'left'),
  cm('arm_left', 'Braço esquerdo', LIMBS, 8, 80, 'right'),
  cm('thigh_right', 'Coxa direita', LIMBS, 10, 120, 'left'),
  cm('thigh_left', 'Coxa esquerda', LIMBS, 10, 120, 'right'),
  cm('calf_right', 'Panturrilha direita', LIMBS, 8, 80, 'left'),
  cm('calf_left', 'Panturrilha esquerda', LIMBS, 8, 80, 'right'),
  cm('arm_right_flexed', 'Braço direito contraído', LIMBS, 8, 80),
  cm('arm_left_flexed', 'Braço esquerdo contraído', LIMBS, 8, 80),
  mm('skinfold_chest', 'Peitoral', SKINFOLDS, 1, 80),
  mm('skinfold_midaxillary', 'Axilar média', SKINFOLDS, 1, 80),
  mm('skinfold_triceps', 'Tríceps', SKINFOLDS, 1, 80),
  mm('skinfold_biceps', 'Bíceps', SKINFOLDS, 1, 80),
  mm('skinfold_subscapular', 'Subescapular', SKINFOLDS, 1, 80),
  mm('skinfold_abdominal', 'Abdominal', SKINFOLDS, 1, 80),
  mm('skinfold_suprailiac', 'Suprailíaca', SKINFOLDS, 1, 80),
  mm('skinfold_thigh', 'Coxa', SKINFOLDS, 1, 80),
  mm('humerus_diameter', 'Úmero', DIAMETERS, 20, 150),
  mm('femur_diameter', 'Fêmur', DIAMETERS, 20, 150),
]);

export const BODY_FAT_SOURCES = Object.freeze([
  { key: 'bioimpedance', label: 'Bioimpedância' },
  { key: 'skinfold', label: 'Adipometria' },
  { key: 'other', label: 'Outro método' },
]);

export function getBodyMeasure(key) {
  return BODY_MEASURES.find((measure) => measure.key === key) || null;
}

export function getBodyFatSourceLabel(key) {
  const source = BODY_FAT_SOURCES.find((item) => item.key === key);
  return source ? source.label : '';
}

/** Catálogo inteiro agrupado por região, na ordem do catálogo. */
export function groupBodyMeasures() {
  return BODY_MEASURE_GROUPS.map((group) => ({
    ...group,
    measures: BODY_MEASURES.filter((measure) => measure.group === group.key),
  }));
}
